import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { MdAddBox, MdDelete, MdContentCopy } from 'react-icons/md';
import FormulaireYScale from './FormulaireYScale/FormulaireYScale';
import FormulaireSelectionDateUnix from '../../../Ajouter/SousComposantTable/SelectionDateForUnixTimestamp/FormulaireSelectionDateUnix';
import Bouton from '../../../../UI/Bouton/Bouton';
import DataGrid from '../../../../UI/DataGrid/DataGrid';
import { stringOrEmptyString } from '../../../../../ressources/functionJS/stringOrEmptyString';
import { unixTimestampToDate } from '../../../../../ressources/functionJS/unixTimestampToDate';
import { FloatChampInput } from '../../../../../ressources/functionJS/FloatChampInput';
import { confirmeOnClick } from '../../../../../ressources/functionJS/confirmeOnClick';
import { checkMot } from '../../../../../ressources/functionJS/checkMot';
import styles from './ModifierComposante.module.scss';

const DATE_FIELDS = ['start_formula', 'end_formula'];
const ALARM_LEVELS = [
  { level: 'high4', color: 'black' },
  { level: 'high3', color: 'red' },
  { level: 'high2', color: 'amber' },
  { level: 'high1', color: 'blue' },
  { level: 'low1', color: 'blue' },
  { level: 'low2', color: 'amber' },
  { level: 'low3', color: 'red' },
  { level: 'low4', color: 'black' },
];

const createBaseColumn = (
  field,
  headerName,
  width = 150,
  type = null,
  formatter = null
) => ({
  field,
  headerName,
  width,
  editable: true,
  sortable: true,
  align: 'center',
  headerAlign: 'center',
  ...(type && { type }),
  ...(formatter && { valueFormatter: (value) => formatter(value) }),
});

const ModifierComposante = ({
  component_list,
  sensor_id,
  gererComposantes,
}) => {
  const [componentList, setComponentList] = useState([]);
  const [increment, setIncrement] = useState(0);

  useEffect(() => {
    // Initialize component list with unique IDs
    const initialList = component_list.map((comp, idx) => ({
      ...comp,
      id: comp.component_id || `temp_${Date.now()}_${idx}`,
    }));
    setComponentList(initialList);
  }, [component_list]);

  const transformComponentForSave = useCallback(
    (comp) => {
      return {
        component_id: isNaN(comp.component_id)
          ? null
          : parseInt(comp.component_id),
        sensor_id: parseInt(sensor_id),
        position_in_sensor: parseInt(comp.position_in_sensor) || null,
        serial_number: stringOrEmptyString(comp.serial_number),
        coordinate_x: parseInt(comp.coordinate_x) || null,
        coordinate_y: parseInt(comp.coordinate_y) || null,
        coordinate_z: parseInt(comp.coordinate_z) || null,
        forumula: stringOrEmptyString(comp.forumula),
        prefix: stringOrEmptyString(comp.prefix),
        engineering_unit: stringOrEmptyString(comp.engineering_unit),
        precision_decimal_notation:
          parseFloat(comp.precision_decimal_notation) || null,
        alarmlevel_high4: parseFloat(comp.alarmlevel_high4) || null,
        alarmlevel_high3: parseFloat(comp.alarmlevel_high3) || null,
        alarmlevel_high2: parseFloat(comp.alarmlevel_high2) || null,
        alarmlevel_high1: parseFloat(comp.alarmlevel_high1) || null,
        alarmlevel_low1: parseFloat(comp.alarmlevel_low1) || null,
        alarmlevel_low2: parseFloat(comp.alarmlevel_low2) || null,
        alarmlevel_low3: parseFloat(comp.alarmlevel_low3) || null,
        alarmlevel_low4: parseFloat(comp.alarmlevel_low4) || null,
        start_formula: parseInt(comp.start_formula) || null,
        end_formula: parseInt(comp.end_formula) || null,
        calibration_factors: stringOrEmptyString(comp.calibration_factors),
        y_scale: comp.y_scale,
        time_period: parseInt(comp.time_period) || null,
        measures_num: parseInt(comp.measures_num) || null,
        time_refresh: parseFloat(comp.time_refresh) || null,
      };
    },
    [sensor_id]
  );

  useEffect(() => {
    if (componentList.length > 0) {
      const transformed = componentList.map(transformComponentForSave);
      gererComposantes(transformed);
    }
  }, [componentList, transformComponentForSave, gererComposantes]);

  const handleDelete = useCallback((componentId) => {
    setComponentList((prev) =>
      prev.filter((c) => c.component_id !== componentId)
    );
  }, []);

  const handleDuplicate = useCallback(
    (component) => {
      const newId = `Nouveau composant n° ${increment}`;
      const duplicated = {
        ...component,
        id: `temp_${Date.now()}_${increment}`,
        component_id: newId,
      };
      setComponentList((prev) => [...prev, duplicated]);
      setIncrement(increment + 1);
    },
    [increment]
  );

  const handleAddRow = useCallback(() => {
    const newId = `Nouveau composant n° ${increment}`;
    const newComponent = {
      id: `temp_${Date.now()}_${increment}`,
      component_id: newId,
      sensor_id: parseInt(sensor_id),
      position_in_sensor: null,
      serial_number: null,
      coordinate_x: null,
      coordinate_y: null,
      coordinate_z: null,
      forumula: null,
      prefix: null,
      engineering_unit: null,
      precision_decimal_notation: null,
      alarmlevel_high4: null,
      alarmlevel_high3: null,
      alarmlevel_high2: null,
      alarmlevel_high1: null,
      alarmlevel_low1: null,
      alarmlevel_low2: null,
      alarmlevel_low3: null,
      alarmlevel_low4: null,
      start_formula: null,
      end_formula: null,
      calibration_factors: null,
      y_scale: [null, null],
      time_period: null,
      measures_num: 100,
      time_refresh: null,
    };
    setComponentList((prev) => [...prev, newComponent]);
    setIncrement(increment + 1);
  }, [increment, sensor_id]);

  const handleRowUpdate = useCallback((newRow) => {
    setComponentList((prev) =>
      prev.map((c) => (c.id === newRow.id ? newRow : c))
    );
    return newRow;
  }, []);

  const columns = useMemo(
    () => [
      {
        field: 'duplicate',
        headerName: checkMot('dupliquer'),
        width: 110,
        editable: false,
        sortable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <Bouton cliquer={() => handleDuplicate(params.row)}>
            <MdContentCopy size={18} />
          </Bouton>
        ),
      },
      {
        field: 'delete',
        headerName: checkMot('supprimer'),
        width: 110,
        editable: false,
        sortable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <Bouton
            cliquer={() =>
              confirmeOnClick(
                checkMot('voulez_vous_vraiment_supprimer_cette_composante'),
                () => handleDelete(params.row.component_id)
              )
            }
          >
            <MdDelete size={20} />
          </Bouton>
        ),
      },
      {
        ...createBaseColumn('component_id', checkMot('id_composante'), 150),
        hide: true,
      },
      createBaseColumn(
        'position_in_sensor',
        checkMot('position_in_sensor'),
        150,
        'number'
      ),
      createBaseColumn('forumula', checkMot('forumula'), 200),
      createBaseColumn('prefix', checkMot('prefix'), 120),
      createBaseColumn('engineering_unit', checkMot('unite'), 140),
      createBaseColumn(
        'precision_decimal_notation',
        checkMot('resolution_decimales'),
        160,
        'number',
        FloatChampInput
      ),
      ...DATE_FIELDS.map((field) => ({
        ...createBaseColumn(
          field,
          checkMot(field),
          180,
          'number',
          unixTimestampToDate
        ),
        renderEditCell: (params) => (
          <FormulaireSelectionDateUnix
            {...params}
            defaultValue={params.value}
            value={params.value}
          />
        ),
      })),
      createBaseColumn(
        'calibration_factors',
        checkMot('calibration_factors'),
        160
      ),
      {
        ...createBaseColumn('y_scale', checkMot('y_scale'), 150),
        valueFormatter: (value) => {
          if (!Array.isArray(value)) return '[aucun]';
          return `[${value[0] ?? 'null'}, ${value[1] ?? 'null'}]`;
        },
        renderEditCell: (params) => (
          <FormulaireYScale
            {...params}
            defaultValue={params.value}
            value={params.value}
          />
        ),
      },
      createBaseColumn('time_period', checkMot('time_period'), 130, 'number'),
      createBaseColumn('measures_num', checkMot('measures_num'), 140, 'number'),
      createBaseColumn('serial_number', checkMot('serial_number'), 140),
      createBaseColumn('coordinate_x', checkMot('coordinate_x'), 130, 'number'),
      createBaseColumn('coordinate_y', checkMot('coordinate_y'), 130, 'number'),
      createBaseColumn('coordinate_z', checkMot('coordinate_z'), 130, 'number'),
      ...ALARM_LEVELS.map(({ level, color }) =>
        createBaseColumn(
          `alarmlevel_${level}`,
          checkMot(`alarmlevel_${level}_${color}`),
          150,
          'number'
        )
      ),
      createBaseColumn('time_refresh', checkMot('time_refresh'), 130, 'number'),
    ],
    [handleDelete, handleDuplicate]
  );

  return (
    <Fragment>
      <div className={styles.container}>
        <DataGrid
          rows={componentList}
          columns={columns}
          getRowId={(row) => row.id}
          editMode="cell"
          processRowUpdate={handleRowUpdate}
          pageSizeOptions={[10, 25, 50]}
          initialPageSize={10}
          disableRowSelectionOnClick
          autoHeight
        />
      </div>
      <button onClick={handleAddRow} className={styles.btn_stylo}>
        <MdAddBox />
        {checkMot('ajouter_une_composante')}
      </button>
    </Fragment>
  );
};

export default ModifierComposante;
